import styled from '@emotion/styled';
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  HistoryOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { backendUrl, SHOW_STOCK_ALERT } from 'config';
import { useAppSelector } from 'store';
import { getProductById } from 'services/productApi';
import {
  useAdjustSkuStock,
  useDeleteSafeStock,
  useSkuStockTransactions,
  useUpsertSafeStock,
} from 'hooks/useProduct';
import { numberFormatInt } from 'utils/nuberFormat';
import { createInputNumberGuards } from 'utils';
import type { InventorySkuItem, ProductInventoryItem } from 'services/interface';
import { SafeProductImage } from './SafeProductImage';
import StockImageThumb from './StockImageThumb';
import {
  formatGeneralStockDisplayName,
  inventorySkuFromProductByIdItem,
  isInventoryGeneralItem,
  mergeProductInventoryFromById,
  type InventoryListResponse,
  type InventoryProduct,
  type ProductByIdDetail,
  type ProductByIdSkuItem,
} from './inventoryHelpers';
import {
  MASTER_STOCK_LABEL,
  STOCK_MODE_ADVANCED_DESC,
  STOCK_MODE_ADVANCED_LABEL,
  STOCK_MODE_BASIC_DESC,
  STOCK_MODE_BASIC_LABEL,
} from './Recipe/bundleLabels';

/** แถวในตารางมาสเตอร์สต็อก — รวม general + sku ไว้ในตารางเดียว (discriminated ด้วย kind) */
type GeneralRow = {
  kind: 'general';
  key: string;
  general: ProductInventoryItem;
};
type SkuRow = {
  kind: 'sku';
  key: string;
  sku: InventorySkuItem;
  product: ProductInventoryItem;
  variantName: string;
};
export type MasterStockRow = GeneralRow | SkuRow;

/** ฟิลด์สต็อกที่ sort ได้ (ทำที่ backend) */
export type MasterStockSortField =
  | 'stock_quantity'
  | 'holding_quantity'
  | 'stock_balance';
export type MasterStockSortDir = 'asc' | 'desc';

interface MasterStockTableProps {
  /** รายการรวม (มาสเตอร์ + SKU) เรียงตาม backend แล้ว — จะถูกแตกเป็นแถว */
  items: ProductInventoryItem[];
  /** general row actions — index เป็นเจ้าของ modal เดิม */
  onAdjustGeneral: (item: ProductInventoryItem) => void;
  onHistoryGeneral: (item: ProductInventoryItem) => void;
  onSafeGeneral: (item: ProductInventoryItem) => void;
  onEditGeneral: (item: ProductInventoryItem) => void;
  onDeleteGeneral: (item: ProductInventoryItem) => void;
  deletingGeneral: boolean;
  /** sku row actions */
  onViewProduct: (productId: string) => void;
  /** เปิด modal กำหนดสินค้า–ราคา/คลัง (ใช้กับ SKU ขั้นสูงที่ปรับสต็อกตรงไม่ได้) */
  onEditSku: (skuId: string, productId: string) => void;
  /** สถานะ sort ปัจจุบัน (controlled) + callback เมื่อกดหัวคอลัมน์ */
  sortBy: MasterStockSortField | null;
  sortDir: MasterStockSortDir | null;
  onSortChange: (
    field: MasterStockSortField | null,
    dir: MasterStockSortDir | null,
  ) => void;
  /** กำลังโหลดข้อมูล (เช่นตอนเปลี่ยน sort) — แสดง spinner เฉพาะส่วน data ไม่ให้หัวคอลัมน์หาย */
  loading?: boolean;
}

const resolveSkuImageSrc = (record: {
  image_id?: string;
  image_url?: string;
}): string | undefined => {
  if (record.image_id) return `${backendUrl}images/${record.image_id}`;
  return record.image_url || undefined;
};

/** รูปมาสเตอร์สต็อก — ใช้ main_images_url จาก API ก่อน (backend ส่ง URL เต็มมาแล้ว)
 *  ไม่มีค่อยประกอบจาก image id (general อัปโหลด/เสิร์ฟผ่าน /images/:id) */
const resolveGeneralImageSrc = (item: ProductInventoryItem): string | undefined => {
  const url = item.main_images_url?.[0];
  if (url) return url;
  const id = item.main_images?.[0];
  return id ? `${backendUrl}images/${id}` : undefined;
};

const MasterStockTable: FC<MasterStockTableProps> = ({
  items,
  onAdjustGeneral,
  onHistoryGeneral,
  onSafeGeneral,
  onEditGeneral,
  onDeleteGeneral,
  deletingGeneral,
  onViewProduct,
  onEditSku,
  sortBy,
  sortDir,
  onSortChange,
  loading = false,
}) => {
  const queryClient = useQueryClient();
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id ?? '',
  );
  // จำนวนสต็อกรับได้เฉพาะจำนวนเต็ม (กันพิมพ์จุดทศนิยม/ตัวอักษร)
  const integerGuards = createInputNumberGuards({ mode: 'integer' });

  // ───────── แถว: ไล่ตามลำดับ items จาก backend (รองรับ sort ฝั่ง backend) ─────────
  const rows: MasterStockRow[] = useMemo(() => {
    const out: MasterStockRow[] = [];
    for (const item of items) {
      if (isInventoryGeneralItem(item)) {
        out.push({ kind: 'general', key: `g-${item.id}`, general: item });
        continue;
      }
      for (const variant of item.variants ?? []) {
        for (const sku of variant.items ?? []) {
          out.push({
            kind: 'sku',
            key: `s-${sku.id}`,
            sku,
            product: item,
            variantName: variant.name,
          });
        }
      }
    }
    return out;
  }, [items]);

  // ───────── SKU stock actions (mirror ProductInventoryGrid, ไม่แตะตัวมัน) ─────────
  const { mutateAsync: adjustSkuApi, isPending: isAdjustingSku } = useAdjustSkuStock();
  const { mutateAsync: upsertSafeApi, isPending: isSavingSafe } = useUpsertSafeStock();
  const { mutateAsync: deleteSafeApi, isPending: isDeletingSafe } = useDeleteSafeStock();

  const [skuAdjustTarget, setSkuAdjustTarget] = useState<SkuRow | null>(null);
  const [skuAdjustForm] = Form.useForm<{
    direction: 'in' | 'out';
    quantity: number;
    remark?: string;
  }>();

  const [skuHistoryTarget, setSkuHistoryTarget] = useState<SkuRow | null>(null);
  const [skuHistoryPage, setSkuHistoryPage] = useState(1);
  const skuHistoryLimit = 10;

  const [skuSafeTarget, setSkuSafeTarget] = useState<SkuRow | null>(null);
  const [skuSafeForm] = Form.useForm<{ is_active: boolean; min_quantity: number }>();

  const skuTxInput = useMemo(
    () => ({
      product_id: skuHistoryTarget?.product.id ?? '',
      sku_id: skuHistoryTarget?.sku.id ?? '',
      page: skuHistoryPage,
      limit: skuHistoryLimit,
    }),
    [skuHistoryTarget?.product.id, skuHistoryTarget?.sku.id, skuHistoryPage],
  );

  const { data: skuTxRes, isFetching: skuTxLoading } = useSkuStockTransactions(
    skuTxInput,
    !!skuHistoryTarget?.product.id && !!skuHistoryTarget?.sku.id,
  );

  /** อัปเดตเฉพาะ SKU แถวนั้นในแคช inventory — เส้นเดียวกับ ProductInventoryGrid */
  const refreshInventorySkuRow = useCallback(
    async (skuId: string, productId: string) => {
      if (!factoryId || !productId) return;
      const fresh = await queryClient.fetchQuery({
        queryKey: ['product', productId],
        queryFn: () => getProductById(productId),
      });
      queryClient.setQueryData(['product', productId], fresh);

      const findItem = (
        product: ProductByIdDetail | undefined,
        id: string,
      ): ProductByIdSkuItem | null => {
        for (const v of product?.variants ?? []) {
          for (const it of v.items ?? []) {
            if (it.id === id) return it;
          }
        }
        return null;
      };

      const freshItemRaw = findItem(fresh.product, skuId);
      const freshItem = freshItemRaw
        ? inventorySkuFromProductByIdItem(freshItemRaw)
        : null;

      // ใช้ setQueriesData (prefix) เพื่ออัปเดตทั้ง cache ปกติและ cache ที่มี sort suffix
      queryClient.setQueriesData(
        { queryKey: ['inventoryByFactory', factoryId] },
        (old: unknown) => {
          if (!old || typeof old !== 'object' || !('products' in old)) return old;
          const prev = old as InventoryListResponse;
          if (!Array.isArray(prev.products)) return old;

          const products = prev.products.map((p: InventoryProduct) => {
            if (p.id !== productId) return p;
            if (!freshItem) return mergeProductInventoryFromById(p, fresh.product);

            let found = false;
            const nextVariants = (p.variants ?? []).map((v) => {
              const items = (v.items ?? []).map((it) => {
                if (it.id !== skuId) return it;
                found = true;
                return freshItem;
              });
              return { ...v, items };
            });
            if (!found) return mergeProductInventoryFromById(p, fresh.product);
            return { ...p, variants: nextVariants };
          });

          return { ...prev, products };
        },
      );
    },
    [factoryId, queryClient],
  );

  const openSkuAdjust = (row: SkuRow) => {
    setSkuAdjustTarget(row);
    skuAdjustForm.setFieldsValue({ direction: 'in', quantity: 1, remark: '' });
  };

  const submitSkuAdjust = async () => {
    if (!skuAdjustTarget) return;
    const { product, sku } = skuAdjustTarget;
    try {
      const v = await skuAdjustForm.validateFields();
      await adjustSkuApi({
        sku_id: sku.id,
        direction: v.direction,
        quantity: v.quantity,
        remark: v.remark,
      });
      message.success(v.direction === 'in' ? 'เพิ่มสต็อกเรียบร้อย' : 'ลดสต็อกเรียบร้อย');
      await refreshInventorySkuRow(sku.id, product.id);
      await queryClient.invalidateQueries({
        queryKey: ['skuStockTransactions', product.id, sku.id],
      });
      setSkuAdjustTarget(null);
    } catch (err: unknown) {
      const e = err as { errorFields?: unknown; message?: string };
      if (e?.errorFields) return;
      message.error(e?.message || 'ไม่สามารถปรับสต็อกได้');
    }
  };

  const openSkuSafe = (row: SkuRow) => {
    setSkuSafeTarget(row);
    const st = row.sku.stock;
    skuSafeForm.setFieldsValue({
      is_active: st?.is_safe_stock_enabled ?? false,
      min_quantity: st?.safe_stock_quantity ?? 0,
    });
  };

  const submitSkuSafe = async () => {
    if (!skuSafeTarget) return;
    const { product, sku } = skuSafeTarget;
    try {
      const v = await skuSafeForm.validateFields();
      await upsertSafeApi({
        product_id: product.id,
        sku_id: sku.id,
        min_quantity: v.min_quantity,
        is_active: v.is_active,
      });
      message.success('บันทึกการแจ้งเตือนสต็อกแล้ว');
      await refreshInventorySkuRow(sku.id, product.id);
      setSkuSafeTarget(null);
    } catch (err: unknown) {
      const e = err as { errorFields?: unknown; message?: string };
      if (e?.errorFields) return;
      message.error(e?.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const clearSkuSafe = async () => {
    if (!skuSafeTarget) return;
    const { product, sku } = skuSafeTarget;
    try {
      await deleteSafeApi({ product_id: product.id, sku_id: sku.id });
      message.success('ปิดการแจ้งเตือนสต็อกแล้ว');
      await refreshInventorySkuRow(sku.id, product.id);
      setSkuSafeTarget(null);
    } catch (err: unknown) {
      const e = err as { message?: string };
      message.error(e?.message || 'ดำเนินการไม่สำเร็จ');
    }
  };

  // ───────── สต็อกของแต่ละแถว (มาสเตอร์ใช้ general.stock, SKU ใช้ sku.stock) ─────────
  const rowStock = (row: MasterStockRow) =>
    row.kind === 'general' ? row.general.stock : row.sku.stock;
  /** SKU ที่ยังไม่เปิดสต็อก → ไม่มีค่าสต็อกให้แสดง */
  const rowHasNoStock = (row: MasterStockRow) =>
    row.kind === 'sku' && !row.sku.is_stock;

  const sortOrderOf = (field: MasterStockSortField) =>
    sortBy === field ? (sortDir === 'asc' ? 'ascend' : 'descend') : null;

  const renderStockNumberCell = (
    row: MasterStockRow,
    field: MasterStockSortField,
    tone: 'default' | 'hold' | 'balance',
  ) => {
    if (rowHasNoStock(row)) {
      return (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          —
        </Typography.Text>
      );
    }
    // สต็อกแบบคำนวณ: ไม่แสดง "คงเหลือ" / "กำลังมีคนซื้อ" (เป็นยอดที่คำนวณจากสต็อกหลัก)
    const isAdvanced = row.kind === 'sku' && row.sku.stock_mode === 'advanced';
    if (isAdvanced && tone !== 'balance') {
      return (
        <Tooltip title="สต็อกแบบคำนวณจากสต็อกหลัก จึงไม่มีจำนวนคงเหลือ/กำลังมีคนซื้อของตัวเอง">
          <Typography.Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
            —
          </Typography.Text>
        </Tooltip>
      );
    }
    const value = rowStock(row)?.[field] ?? 0;
    return (
      <StockStatValue
        $tone={tone}
        $positive={tone === 'balance' ? value > 0 : undefined}
      >
        {numberFormatInt(value)}
      </StockStatValue>
    );
  };

  const renderSafeCell = (
    stock:
      | { is_safe_stock_enabled?: boolean; safe_stock_quantity?: number; stock_balance?: number }
      | undefined,
    onSetting: () => void,
  ) => {
    const enabled = stock?.is_safe_stock_enabled ?? false;
    const minQ = stock?.safe_stock_quantity ?? 0;
    const bal = stock?.stock_balance ?? 0;
    const triggered = enabled && minQ > 0 && bal <= minQ;
    return (
      <SafeCol>
        {enabled && minQ > 0 ? (
          <Tooltip title={`แจ้งเตือนเมื่อพร้อมขาย ≤ ${numberFormatInt(minQ)}`}>
            <Tag
              color={triggered ? 'error' : 'gold'}
              style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}
            >
              <BellOutlined style={{ fontSize: 11 }} /> ≤{numberFormatInt(minQ)}
            </Tag>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 11, lineHeight: '16px' }}>
            ปิด
          </Typography.Text>
        )}
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 20, fontSize: 11, lineHeight: 1 }}
          onClick={onSetting}
        >
          ตั้งค่า
        </Button>
      </SafeCol>
    );
  };

  const columns: ColumnsType<MasterStockRow> = useMemo(
    () => [
      {
        title: MASTER_STOCK_LABEL,
        key: 'info',
        width: 300,
        render: (_: unknown, row: MasterStockRow) => {
          if (row.kind === 'general') {
            const item = row.general;
            return (
              <InfoCell>
                <ThumbBox>
                  <StockImageThumb src={resolveGeneralImageSrc(item)} alt={item.name ?? ''} />
                </ThumbBox>
                <div style={{ minWidth: 0 }}>
                  <TitleText strong>
                    {formatGeneralStockDisplayName(item.name, item.description)}
                  </TitleText>
                  <TagRow>
                    <Tag
                      color={item.is_active !== false ? 'green' : 'default'}
                      style={{ fontSize: 11, margin: 0 }}
                    >
                      {item.is_active !== false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </Tag>
                  </TagRow>
                </div>
              </InfoCell>
            );
          }
          const { sku, product } = row;
          const effectiveActive =
            product.is_active !== false && sku.is_active !== false;
          return (
            <InfoCell>
              <ThumbBox>
                <SafeProductImage
                  src={resolveSkuImageSrc(sku)}
                  alt={sku.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </ThumbBox>
              <div style={{ minWidth: 0 }}>
                <ParentRow>
                  <ParentName title={product.name}>{product.name}</ParentName>
                </ParentRow>
                <TitleText strong title={sku.name}>
                  {sku.name}
                </TitleText>
                {sku.product_code && <SkuCode>รหัส: {sku.product_code}</SkuCode>}
                <TagRow>
                  <Tag
                    color={effectiveActive ? 'green' : 'default'}
                    style={{ fontSize: 11, margin: 0 }}
                  >
                    {effectiveActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                  </Tag>
                  {sku.is_stock && sku.stock_mode ? (
                    <Tooltip
                      title={
                        sku.stock_mode === 'advanced'
                          ? STOCK_MODE_ADVANCED_DESC
                          : STOCK_MODE_BASIC_DESC
                      }
                    >
                      <Tag
                        color={sku.stock_mode === 'advanced' ? 'purple' : 'blue'}
                        style={{ fontSize: 11, margin: 0, cursor: 'help' }}
                      >
                        {sku.stock_mode === 'advanced'
                          ? STOCK_MODE_ADVANCED_LABEL
                          : STOCK_MODE_BASIC_LABEL}
                      </Tag>
                    </Tooltip>
                  ) : null}
                </TagRow>
              </div>
            </InfoCell>
          );
        },
      },
      {
        title: 'คงเหลือ',
        key: 'stock_quantity',
        align: 'center' as const,
        width: 110,
        sorter: true,
        sortOrder: sortOrderOf('stock_quantity'),
        render: (_: unknown, row: MasterStockRow) =>
          renderStockNumberCell(row, 'stock_quantity', 'default'),
      },
      {
        title: 'กำลังมีคนซื้อ',
        key: 'holding_quantity',
        align: 'center' as const,
        width: 120,
        sorter: true,
        sortOrder: sortOrderOf('holding_quantity'),
        render: (_: unknown, row: MasterStockRow) =>
          renderStockNumberCell(row, 'holding_quantity', 'hold'),
      },
      {
        title: 'พร้อมใช้',
        key: 'stock_balance',
        align: 'center' as const,
        width: 110,
        sorter: true,
        sortOrder: sortOrderOf('stock_balance'),
        render: (_: unknown, row: MasterStockRow) =>
          renderStockNumberCell(row, 'stock_balance', 'balance'),
      },
      // ซ่อน "แจ้งเตือนสต็อก" ไว้ก่อน (SHOW_STOCK_ALERT = false)
      ...(SHOW_STOCK_ALERT ? [{
        title: 'แจ้งเตือนสต็อก',
        key: 'safe',
        align: 'center' as const,
        width: 118,
        render: (_: unknown, row: MasterStockRow) => {
          if (row.kind === 'general') {
            return renderSafeCell(row.general.stock, () => onSafeGeneral(row.general));
          }
          if (!row.sku.is_stock) {
            return (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                —
              </Typography.Text>
            );
          }
          return renderSafeCell(row.sku.stock, () => openSkuSafe(row));
        },
      }] : []) as ColumnsType<MasterStockRow>,
      {
        title: 'การจัดการ',
        key: 'actions',
        width: 120,
        fixed: 'right' as const,
        render: (_: unknown, row: MasterStockRow) => {
          if (row.kind === 'general') {
            return (
              <ActionCol>
                <Button
                  type="link"
                  size="small"
                  icon={<ToolOutlined style={{ fontSize: 13 }} />}
                  disabled={!row.general.stock?.id}
                  onClick={() => onAdjustGeneral(row.general)}
                  style={{ padding: '0 2px', height: 22, fontSize: 12, lineHeight: 1 }}
                >
                  ปรับสต็อก
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<HistoryOutlined style={{ fontSize: 13 }} />}
                  disabled={!row.general.stock?.id}
                  onClick={() => onHistoryGeneral(row.general)}
                  style={{ padding: '0 2px', height: 22, fontSize: 12, lineHeight: 1 }}
                >
                  ประวัติ
                </Button>
              </ActionCol>
            );
          }
          if (!row.sku.is_stock) {
            return (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                —
              </Typography.Text>
            );
          }
          const isAdvanced = row.sku.stock_mode === 'advanced';
          // มีสต็อก SKU ตัวเองเป็นส่วนประกอบ → ปรับสต็อกพื้นฐานของตัวเองได้ (basic เป็น true เสมอ)
          // ชุดเซ็ต (ขั้นสูง) ไม่แสดงปุ่มปรับสต็อก/ประวัติ — จัดการผ่านส่วนประกอบที่ผูกไว้แทน
          const canAdjustSelf = !isAdvanced && row.sku.stock_has_self !== false;
          return (
            <ActionCol>
              {canAdjustSelf ? (
                <>
                  <Button
                    type="link"
                    size="small"
                    icon={<ToolOutlined style={{ fontSize: 13 }} />}
                    onClick={() => openSkuAdjust(row)}
                    style={{ padding: '0 2px', height: 22, fontSize: 12, lineHeight: 1 }}
                  >
                    ปรับสต็อก
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    icon={<HistoryOutlined style={{ fontSize: 13 }} />}
                    onClick={() => {
                      setSkuHistoryTarget(row);
                      setSkuHistoryPage(1);
                    }}
                    style={{ padding: '0 2px', height: 22, fontSize: 12, lineHeight: 1 }}
                  >
                    ประวัติ
                  </Button>
                </>
              ) : null}
              {isAdvanced ? (
                <Tooltip title="แก้ไขสต็อกหลักที่ผูกไว้ (ราคา/คลัง) ผ่านหน้าแก้ไข">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined style={{ fontSize: 13 }} />}
                    onClick={() => onEditSku(row.sku.id, row.product.id)}
                    style={{ padding: '0 2px', height: 22, fontSize: 12, lineHeight: 1 }}
                  >
                    แก้ไข
                  </Button>
                </Tooltip>
              ) : null}
            </ActionCol>
          );
        },
      },
      {
        title: 'จัดการสินค้า',
        key: 'crud',
        align: 'center' as const,
        width: 90,
        fixed: 'right' as const,
        render: (_: unknown, row: MasterStockRow) => {
          if (row.kind === 'general') {
            return (
              <CrudCol>
                <Tooltip title="แก้ไขข้อมูลทั่วไป">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    aria-label="แก้ไขข้อมูลทั่วไป"
                    onClick={() => onEditGeneral(row.general)}
                  />
                </Tooltip>
                <Popconfirm
                  title="ลบรายการนี้?"
                  okText="ลบ"
                  cancelText="ยกเลิก"
                  onConfirm={() => onDeleteGeneral(row.general)}
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deletingGeneral}
                    aria-label="ลบ"
                  />
                </Popconfirm>
              </CrudCol>
            );
          }
          // แถว SKU: ไปหน้าแก้ไขสินค้า (ย้ายไอคอนมาจากชื่อสินค้า)
          return (
            <CrudCol>
              <Tooltip title="ไปหน้าแก้ไขสินค้า">
                <Button
                  type="text"
                  size="small"
                  icon={<ExportOutlined />}
                  aria-label="ไปหน้าแก้ไขสินค้า"
                  onClick={() => onViewProduct(row.product.id)}
                />
              </Tooltip>
            </CrudCol>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deletingGeneral,
      onAdjustGeneral,
      onHistoryGeneral,
      onSafeGeneral,
      onEditGeneral,
      onDeleteGeneral,
      onEditSku,
      onViewProduct,
      sortBy,
      sortDir,
    ],
  );

  const skuTxRows = skuTxRes?.data?.rows ?? [];
  const skuTxTotal = skuTxRes?.data?.total ?? 0;

  const skuTxColumns = useMemo(
    () => [
      {
        title: 'วันที่',
        dataIndex: 'create_date',
        key: 'create_date',
        width: 160,
        render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        title: 'การกระทำ',
        dataIndex: 'direction',
        key: 'direction',
        width: 90,
        render: (d: string) =>
          d === 'in' ? <Tag color="blue">เข้า</Tag> : <Tag color="volcano">ออก</Tag>,
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 90,
        render: (q: number) => numberFormatInt(q ?? 0),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
      },
    ],
    [],
  );

  return (
    <TableWrap>
      <Table
        rowKey="key"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ x: 980 }}
        onChange={(_pagination, _filters, sorter) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          const key = s?.columnKey as MasterStockSortField | undefined;
          const order = s?.order;
          if (key && order) {
            onSortChange(key, order === 'ascend' ? 'asc' : 'desc');
          } else {
            onSortChange(null, null);
          }
        }}
        rowClassName={(row) =>
          row.kind === 'sku' && !row.sku.is_stock ? 'row-no-stock' : ''
        }
      />

      {/* ปรับสต็อก SKU */}
      <Modal
        title={
          skuAdjustTarget ? (
            <span>
              ปรับสต็อก —{' '}
              <Typography.Text strong>{skuAdjustTarget.sku.name}</Typography.Text>
            </span>
          ) : (
            'ปรับสต็อก'
          )
        }
        open={!!skuAdjustTarget}
        maskClosable={!isAdjustingSku}
        onCancel={() => {
          if (isAdjustingSku) return;
          setSkuAdjustTarget(null);
        }}
        onOk={submitSkuAdjust}
        confirmLoading={isAdjustingSku}
        okText="บันทึก"
        cancelText="ปิด"
        destroyOnClose
        width={520}
      >
        <Form
          disabled={isAdjustingSku}
          form={skuAdjustForm}
          layout="vertical"
          initialValues={{ direction: 'in', quantity: 1 }}
        >
          <Form.Item label="คงเหลือปัจจุบัน">
            <Typography.Text strong>
              {numberFormatInt(skuAdjustTarget?.sku.stock?.stock_quantity ?? 0)} ชิ้น
            </Typography.Text>
          </Form.Item>
          <Form.Item label="การทำงาน" name="direction" rules={[{ required: true }]}>
            <Segmented
              block
              options={[
                { label: 'เพิ่มเข้าคลัง', value: 'in' },
                { label: 'นำออกจากคลัง', value: 'out' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="จำนวน"
            name="quantity"
            rules={[
              { required: true, message: 'กรุณาระบุจำนวน' },
              {
                validator: (_, v) =>
                  v >= 1
                    ? Promise.resolve()
                    : Promise.reject(new Error('จำนวนต้องไม่น้อยกว่า 1')),
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              step={1}
              style={{ width: '100%' }}
              {...integerGuards}
            />
          </Form.Item>
          <Form.Item label="หมายเหตุ (ถ้ามี)" name="remark">
            <Input.TextArea
              rows={2}
              placeholder="เช่น รับของจากโรงงาน / ของเสียหาย / นับสต็อก"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ประวัติเข้า-ออก SKU */}
      <Modal
        title={
          skuHistoryTarget ? (
            <span>
              ประวัติเข้า-ออก —{' '}
              <Typography.Text strong>{skuHistoryTarget.sku.name}</Typography.Text>
            </span>
          ) : (
            'ประวัติเข้า-ออก'
          )
        }
        open={!!skuHistoryTarget}
        onCancel={() => setSkuHistoryTarget(null)}
        footer={null}
        destroyOnClose
        width={720}
      >
        <Table
          rowKey="id"
          size="small"
          loading={skuTxLoading}
          dataSource={skuTxRows}
          columns={skuTxColumns}
          pagination={{
            current: skuHistoryPage,
            pageSize: skuHistoryLimit,
            total: skuTxTotal,
            showSizeChanger: false,
            onChange: (p) => setSkuHistoryPage(p),
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          }}
          scroll={{ y: 360 }}
          locale={{ emptyText: 'ยังไม่มีประวัติ' }}
        />
      </Modal>

      {/* แจ้งเตือนสต็อก SKU */}
      <Modal
        title={
          skuSafeTarget ? (
            <span>
              แจ้งเตือนสต็อก —{' '}
              <Typography.Text strong>{skuSafeTarget.sku.name}</Typography.Text>
            </span>
          ) : (
            'แจ้งเตือนสต็อก'
          )
        }
        open={!!skuSafeTarget}
        maskClosable={!(isSavingSafe || isDeletingSafe)}
        onCancel={() => {
          if (isSavingSafe || isDeletingSafe) return;
          setSkuSafeTarget(null);
        }}
        destroyOnClose
        footer={[
          <Button
            key="clear"
            danger
            loading={isDeletingSafe}
            onClick={() => void clearSkuSafe()}
            disabled={!skuSafeTarget || isSavingSafe}
          >
            ล้าง / ปิดการแจ้งเตือน
          </Button>,
          <Button
            key="cancel"
            disabled={isSavingSafe || isDeletingSafe}
            onClick={() => setSkuSafeTarget(null)}
          >
            ยกเลิก
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={isSavingSafe}
            disabled={isDeletingSafe}
            onClick={() => void submitSkuSafe()}
          >
            บันทึก
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          เมื่อเปิดใช้ ระบบจะถือว่าควรแจ้งเตือนเมื่อจำนวน<strong>พร้อมขาย</strong>
          น้อยกว่าหรือเท่ากับเกณฑ์ที่กำหนด
        </Typography.Paragraph>
        <Form
          disabled={isSavingSafe || isDeletingSafe}
          form={skuSafeForm}
          layout="vertical"
          initialValues={{ is_active: false, min_quantity: 0 }}
        >
          <Form.Item label="เปิดการแจ้งเตือน" name="is_active" valuePropName="checked">
            <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
          </Form.Item>
          <Form.Item
            label="เกณฑ์ (ชิ้น) — แจ้งเมื่อพร้อมขาย ≤ ค่านี้"
            name="min_quantity"
            rules={[
              { required: true, message: 'กรุณาระบุจำนวน' },
              {
                validator: (_, v) =>
                  v >= 0 ? Promise.resolve() : Promise.reject(new Error('ต้องไม่ติดลบ')),
              },
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              step={1}
              style={{ width: '100%' }}
              placeholder="เช่น 10"
              {...integerGuards}
            />
          </Form.Item>
        </Form>
      </Modal>
    </TableWrap>
  );
};

export default MasterStockTable;

/** Wrap ให้ table หน้าตาเหมือน SKU grid */
const TableWrap = styled.div`
  .ant-table {
    border-radius: 6px;
    overflow: hidden;
    font-size: 13px;
  }

  .ant-table-thead > tr > th {
    padding: 7px 8px !important;
    font-size: 12px;
    line-height: 1.3;
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    padding: 6px 8px !important;
    line-height: 1.35;
    font-size: 13px;
    vertical-align: middle;
  }

  /* SKU ที่ยังไม่เปิดสต็อก → ทำเป็นสีเทา */
  .row-no-stock > td {
    opacity: 0.55;
    background: #fafafa;
  }
`;

const InfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const ThumbBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  overflow: hidden;
`;

const TitleText = styled(Typography.Text)`
  && {
    display: block;
    font-size: 14px;
    line-height: 1.45;
    word-break: break-word;
  }
`;

/** บรรทัดชื่อสินค้าแม่ + ปุ่มลิงก์ไปหน้าแก้ไขสินค้า */
const ParentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

/** ชื่อสินค้าแม่ของ SKU — บรรทัดบน ตัวเล็กสีเทา (บอกบริบทว่า SKU นี้อยู่ใต้สินค้าไหน) */
const ParentName = styled.div`
  font-size: 11px;
  line-height: 1.35;
  color: #8c8c8c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

/** รหัสสินค้าของ SKU */
const SkuCode = styled.div`
  font-size: 11px;
  line-height: 16px;
  color: #8c8c8c;
`;

const TagRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const StockStatValue = styled.div<{
  $tone: 'default' | 'hold' | 'balance';
  $positive?: boolean;
}>`
  width: 100%;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${(p) => {
    if (p.$tone === 'hold') return '#d46b08';
    if (p.$tone === 'balance') return p.$positive ? '#389e0d' : '#cf1322';
    return '#434343';
  }};
`;

const SafeCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex-shrink: 0;
  width: 88px;
  padding: 0 4px;
`;

const ActionCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const CrudCol = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`;
